// Changing an appointment in the UCO app: PUT /api/appointments/<id> takes the whole
// appointment back, not only what changed. These pure functions build that body from the
// record we already have, and apply the same change to our copy.
//
// No imports on purpose: the update-appointment Edge Function (Deno) and the site (Vite)
// both use this file.

export const APPOINTMENT_STATUSES = ['created', 'postponed', 'completed', 'cancelled'] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export interface UpdateItem {
  itemId: string;
  quantity: number;
  /** name, type and unit, kept in our copy only (the API knows its items) */
  item?: { name?: unknown; type?: string | null; unit?: string | null } | null;
}

/** What the site asks for. Every field but id and status is optional: leave it out to keep it. */
export interface AppointmentUpdate {
  id: string;
  status: AppointmentStatus;
  /** the whole list of items, with the quantities really collected / handed out */
  items?: UpdateItem[];
  /** a note for the delivery person's field */
  driverNote?: string;
  /** a corrected position (placed by hand): sent as a new location code */
  position?: { lat: number; lng: number } | null;
  /** for the collect log only: the program run, and why it was not collected */
  programId?: string;
  reason?: string;
}

/** A row of the collect log (collect_log in schema.sql), without who and when (the database adds them). */
export interface CollectLogInsert {
  appointment_id: string;
  program_id: string | null;
  status: AppointmentStatus;
  reason: string;
  note: string;
  items: UpdateItem[];
}

/** What the collect log keeps of a change: the appointment's items as they now are (`raw` after applyToRaw). */
export function collectLogRow(raw: Raw, u: AppointmentUpdate): CollectLogInsert {
  const items = Array.isArray(raw.declaredItems) ? (raw.declaredItems as UpdateItem[]) : [];
  return {
    appointment_id: u.id,
    program_id: u.programId || null,
    status: u.status,
    reason: u.reason || '',
    note: u.driverNote ?? '',
    items: items.map((i) => ({ itemId: i.itemId, quantity: Number(i.quantity) || 0, item: i.item ? { name: i.item.name, type: i.item.type, unit: i.item.unit } : null })),
  };
}

type Raw = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const idOf = (v: unknown): string | null => {
  const o = v as { id?: unknown } | null;
  return o && typeof o.id === 'string' ? o.id : null;
};

/** "2026-10-26T10:00:00.000Z" -> "2026-10-26T10:00:00", the shape the dashboard sends. */
export function apiDate(v: unknown): string | null {
  const s = str(v);
  if (!s) return null;
  return s.replace(/(\.\d+)?(Z|[+-]\d\d:?\d\d)?$/, '');
}

/** 36.763, 10.29031 -> "LOC-367630-102903" (the code the app writes: 2 + 4 digits each). */
export function toLocationCode(lat: number, lng: number): string {
  const six = (n: number) => String(Math.round(Math.abs(n) * 10000)).padStart(6, '0').slice(0, 6);
  return 'LOC-' + six(lat) + '-' + six(lng);
}

export function isValidUpdate(u: unknown): u is AppointmentUpdate {
  const o = u as AppointmentUpdate | null;
  if (!o || typeof o.id !== 'string' || !o.id) return false;
  if (!(APPOINTMENT_STATUSES as readonly string[]).includes(o.status)) return false;
  if (o.items !== undefined) {
    if (!Array.isArray(o.items)) return false;
    for (const it of o.items) {
      if (!it || typeof it.itemId !== 'string' || !it.itemId) return false;
      if (typeof it.quantity !== 'number' || !isFinite(it.quantity) || it.quantity < 0 || it.quantity > 100000) return false;
    }
  }
  if (o.driverNote !== undefined && (typeof o.driverNote !== 'string' || o.driverNote.length > 2000)) return false;
  if (o.programId !== undefined && (typeof o.programId !== 'string' || o.programId.length > 200)) return false;
  if (o.reason !== undefined && (typeof o.reason !== 'string' || o.reason.length > 60)) return false;
  if (o.position) {
    const { lat, lng } = o.position;
    if (typeof lat !== 'number' || typeof lng !== 'number' || lat < 30 || lat > 38 || lng < 7 || lng > 12) return false;
  }
  return true;
}

/** Items with a quantity: the API drops the others anyway, and so do we. */
const kept = (items: UpdateItem[]) => items.filter((i) => i.quantity > 0);

/**
 * The PUT body. `base` is the freshest copy of the appointment (the API's own answer when it
 * could be read, else our saved copy): every field we do not change is sent back as it is.
 */
export function buildPutPayload(base: Raw, u: AppointmentUpdate): Record<string, unknown> {
  const declared = Array.isArray(base.declaredItems) ? (base.declaredItems as { itemId: string; quantity: unknown }[]) : [];
  const items = u.items ? kept(u.items) : declared.map((d) => ({ itemId: d.itemId, quantity: Number(d.quantity) || 0 }));
  const code = u.position ? toLocationCode(u.position.lat, u.position.lng) : null;
  const body: Record<string, unknown> = {
    status: u.status,
    scheduledDate: apiDate(base.scheduledDate),
    startTime: apiDate(base.startTime) ?? apiDate(base.scheduledDate),
    endTime: apiDate(base.endTime),
    address: code ? 'Location: ' + code : str(base.address),
    locationCode: code ?? str(base.locationCode),
    zoneId: str(base.zoneId) || idOf(base.zone),
    deliveryPersonnelId: str(base.deliveryPersonnelId) || idOf(base.deliveryPersonnel),
    deliveryPersonnelNotes: u.driverNote ?? str(base.deliveryPersonnelNotes),
    notes: str(base.notes),
    delegation: str(base.delegation),
    autoAssignDelivery: base.autoAssignDelivery === true,
    declaredItems: items.map((i) => ({ itemId: i.itemId, quantity: i.quantity })),
  };
  // A field the appointment does not have (no delivery person, no end time…) is left out: the API
  // refuses null ("Invalid input provided") where it accepts a missing field.
  Object.keys(body).forEach((k) => body[k] === null && delete body[k]);
  return body;
}

/** Our copy after the change, so the site shows it at once (the next import confirms it). */
export function applyToRaw<T extends Raw>(raw: T, u: AppointmentUpdate, now: string): T {
  const out: Raw = { ...raw, status: u.status, updatedAt: now };
  if (u.items) {
    const old = new Map(
      (Array.isArray(raw.declaredItems) ? (raw.declaredItems as UpdateItem[]) : []).map((d) => [d.itemId, d] as const),
    );
    out.declaredItems = kept(u.items).map((i) => {
      const prev = old.get(i.itemId);
      return { ...(prev || {}), itemId: i.itemId, quantity: i.quantity, item: (prev && prev.item) || i.item || null };
    });
  }
  if (u.driverNote !== undefined) out.deliveryPersonnelNotes = u.driverNote;
  if (u.position) {
    const code = toLocationCode(u.position.lat, u.position.lng);
    out.locationCode = code;
    out.address = 'Location: ' + code;
  }
  return out as T;
}
